
import React, { useEffect } from 'react';
import type { ParkingReservation } from '../../types';
import { QRCodeSVG } from 'qrcode.react';

export interface ParkingPrintData {
    reservation: ParkingReservation;
    propertyName: string;
}

interface ParkingPrintPageProps extends ParkingPrintData {
    onDone: () => void;
}

const ParkingPrintPage: React.FC<ParkingPrintPageProps> = ({ reservation, propertyName, onDone }) => {
    useEffect(() => {
        const originalBodyStyle = document.body.style.cssText;
        document.body.style.cssText = 'background-color: white;';
        
        const timer = setTimeout(() => {
            window.print();
            onDone();
        }, 500); 

        return () => {
            clearTimeout(timer);
            document.body.style.cssText = originalBodyStyle;
        };
    }, [onDone]);

    return (
        <div className="bg-white min-h-screen text-black font-sans p-4">
            <style type="text/css" media="print">
            {`
                @page { size: A4; margin: 1cm; }
                body { -webkit-print-color-adjust: exact; color-adjust: exact; background-color: white; }
                .print-container { width: 100%; max-width: 210mm; margin: 0 auto; }
            `}
            </style>
            
            <div className="print-container space-y-4">
                {/* SECTION 1: SHUTTLE COUPON */}
                <div className="border-2 border-black p-4 relative">
                    <div className="flex justify-between items-start mb-2">
                        <div>
                            <h1 className="text-xl font-bold leading-tight">7700 COTE DE LIESSE</h1>
                            <h1 className="text-xl font-bold leading-tight">ST-LAURENT, QC H4T 1E7</h1>
                            <p className="text-xs font-bold border-b border-black inline-block mt-1">
                                Billet de Navette Retour – Shuttle Coupon Return
                            </p>
                        </div>
                        <div className="text-right border-2 border-black p-1 w-20 h-12 flex flex-col items-center justify-center">
                            <span className="text-[9px] font-bold leading-none">QUALITY</span>
                            <span className="text-[7px] leading-none">HOTEL</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-xs font-bold">
                        <div className="flex items-end gap-2">
                            <span>Date :</span>
                            <div className="flex-1 border-b border-black pb-0.5">{reservation.startDate}</div>
                        </div>
                        <div className="flex items-end gap-2">
                            <span>Nom Client Name:</span>
                            <div className="flex-1 border-b border-black pb-0.5">{reservation.guestName}</div>
                        </div>
                        <div className="flex items-end gap-2">
                            <span>Chambre / Room:</span>
                            <div className="flex-1 border-b border-black pb-0.5">{reservation.roomNumber}</div>
                        </div>
                        <div className="flex items-end gap-2">
                            <span># de personne / # of people:</span>
                            <div className="flex-1 border-b border-black pb-0.5">{reservation.numberOfPeople}</div>
                        </div>
                        <div className="flex items-end gap-2">
                            <span># de Rés. / Res. #:</span>
                            <div className="flex-1 border-b border-black pb-0.5">{reservation.confirmationNumber}</div>
                        </div>
                        <div className="flex items-end gap-2">
                            <span>Autorisé par / Authorized by :</span>
                            <div className="flex-1 border-b border-black pb-0.5">{reservation.authorizedBy}</div>
                        </div>
                    </div>

                    <div className="mt-4 space-y-2 text-[10px] leading-tight">
                        <p>
                            Lorsque vous avez votre baggage en main à l’aéroport, appeler à l’hôtel au <span className="font-bold underline">514-731-7821 #2</span> et attendre à la <span className="font-bold underline">porte 8 au 2<sup>ème</sup> étage</span>. Merci 😊
                        </p>
                        <p className="font-bold">Please return this coupon to the Shuttle Driver</p>
                        <p>
                            When you have your luggage in hand at the airport, please call hotel at <span className="font-bold underline">1-800-361-6243 #2</span> and wait at <span className="font-bold underline">door 8 on the 2nd floor</span>. Thank you 😊
                        </p>
                    </div>
                </div>

                <div className="border-t-2 border-dashed border-gray-400 my-2"></div>

                {/* SECTION 2: PARKING PERMIT */}
                <div className="border-2 border-black p-4 text-center">
                    <div className="flex justify-between items-center mb-4">
                        <div className="border-2 border-black inline-block px-4 py-1">
                            <div className="flex items-center justify-center gap-2">
                                <div className="w-6 h-6 border-4 border-black rounded-full border-t-transparent rotate-45"></div>
                                <div className="text-left">
                                    <div className="text-lg font-bold leading-tight uppercase">{reservation.reservationSource}</div>
                                </div>
                            </div>
                        </div>
                        <div className="text-right">
                            <h2 className="text-xl font-bold">
                                PERMIS DE STATIONNEMENT<br />
                                PARKING PERMIT
                            </h2>
                        </div>
                    </div>

                    <div className="border-2 border-black p-2 mb-4 text-xs font-bold bg-gray-50">
                        <p>SVP, PLACEZ-LE DU COTE DU CONDUCTEUR SUR LE TABLEAU DE BORD</p>
                        <p className="text-[10px]">PLEASE, PLACE IT ON THE DRIVER SIDE ON THE DASHBOARD</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4 max-w-2xl mx-auto">
                        <div className="border-2 border-black rounded-xl p-2">
                            <p className="text-[10px] font-bold">Date de retour / Returning date:</p>
                            <p className="text-lg font-bold">{reservation.endDate}</p>
                        </div>
                        <div className="border-2 border-black rounded-xl p-2">
                            <p className="text-[10px] font-bold">Plaque d'immatriculation / License Plate:</p>
                            <p className="text-lg font-bold uppercase">{reservation.licensePlate}</p>
                        </div>
                        <div className="border-2 border-black rounded-xl p-2">
                            <p className="text-[10px] font-bold">CONFIRMATION:</p>
                            <p className="text-lg font-bold">{reservation.confirmationNumber}</p>
                        </div>
                        <div className="border-2 border-black rounded-xl p-2">
                            <p className="text-[10px] font-bold">Autorisé par / Authorized by:</p>
                            <p className="text-lg font-bold">{reservation.authorizedBy}</p>
                        </div>
                    </div>
                </div>

                <div className="border-t-2 border-dashed border-gray-400 my-2"></div>

                {/* SECTION 3: QR CODE */}
                <div className="flex items-center justify-around border-2 border-black p-4">
                    <div className="text-left">
                        <h3 className="text-xl font-bold">Reservation Code</h3>
                        <p className="text-2xl font-black text-indigo-600">{reservation.confirmationNumber}</p>
                        <p className="text-sm text-gray-500 mt-1">Scan for verification</p>
                    </div>
                    <div className="bg-white p-2 border border-gray-200 shadow-sm">
                        <QRCodeSVG 
                            value={reservation.confirmationNumber} 
                            size={120}
                            level="H"
                            includeMargin={false}
                        />
                    </div>
                </div>
            </div>

            {/* CUT LINES INDICATOR */}
            <div className="fixed bottom-4 left-0 right-0 text-center text-[10px] text-gray-400 print:hidden">
                A4 Print Preview - Sections are designed to be cut if needed
            </div>
        </div>
    );
};

export default ParkingPrintPage;
